import classNames from "classnames";
import { type ReactNode } from "react";
import classes from "~/features/configurator/Shaft/substeps/ShaftMachiningSubstep/TileCarousel/TileCarousel.module.less";
import { SideButton } from "~/common/components/Button/SideButton";
import { IconType } from "~/common/components/icon/IconType";

type Data = {
  value: string;
  disabled?: boolean;
};

interface RenderProps<T extends Data> {
  data: T;
  onClick?: () => void;
  disabled?: boolean;
  selected: boolean;
  error?: boolean;
}

interface Props<T extends Data> {
  className?: string;
  values: T[];
  renderTile: (props: RenderProps<T>) => ReactNode;
  value: string;
  onChange?: (value: string) => void;
  maxVisiblePerSide?: number;
  error?: boolean;
  disabled?: boolean;
}

export const TileCarousel = <T extends Data>({
  onChange,
  className,
  values,
  renderTile,
  value,
  maxVisiblePerSide = 2,
  error,
  disabled: externalDisabled,
}: Props<T>) => {
  const initialMaxVisiblePerSide = maxVisiblePerSide;
  const valueIndex = values.findIndex((v) => v.value === value);
  let maxVisibleTiles = 2 * maxVisiblePerSide + 1;
  if (maxVisibleTiles - values.length > 1) {
    // Round down to the nearest even number
    const tooMany = Math.floor((maxVisibleTiles - values.length) / 2);
    maxVisiblePerSide = maxVisiblePerSide - tooMany;
    maxVisibleTiles = 2 * maxVisiblePerSide + 1;
  }
  const needsFiller = maxVisibleTiles - values.length > 0;

  let centerValueIndex;
  const selectedIndex = values.findIndex((v) => v.value === value);
  if (selectedIndex >= 0) {
    centerValueIndex = selectedIndex;
  } else {
    centerValueIndex = 0;
  }
  const startIndex = centerValueIndex - maxVisiblePerSide + (needsFiller ? 1 : 0);
  const endIndex = centerValueIndex + maxVisiblePerSide;
  const visibleTiles = [];
  if (startIndex < 0) {
    visibleTiles.push(...values.slice(startIndex), ...values.slice(0, endIndex + 1));
  } else if (endIndex >= values.length) {
    visibleTiles.push(...values.slice(startIndex), ...values.slice(0, endIndex - values.length + 1));
  } else {
    visibleTiles.push(...values.slice(startIndex, endIndex + 1));
  }

  const centerValue = values[centerValueIndex];
  const newCenterValueIndex = visibleTiles.findIndex((v) => v === centerValue);

  const emptyTileData = {
    data: values[0],
    onClick: () => {},
    selected: false,
  };

  const renderNextButton = () => {
    const nextTileList = [...values.slice(centerValueIndex + 1), ...values.slice(0, centerValueIndex)];
    const nextTile = nextTileList.find((tile) => !tile.disabled);
    const disabled = !nextTile || nextTile.value === value;
    const onClick = () => {
      onChange?.(nextTile?.value ?? "");
    };
    return (
      <SideButton
        className={classes.tileButton}
        icon={IconType.ChevronDown}
        color={"primary"}
        disabled={disabled || externalDisabled}
        onClick={onClick}
      />
    );
  };

  const renderPrevButton = () => {
    let previousTileList;
    if (centerValueIndex === 0) {
      previousTileList = [...values];
    } else {
      previousTileList = [...values.slice(centerValueIndex), ...values.slice(0, centerValueIndex)];
    }
    previousTileList.reverse();
    const prevTile = previousTileList.find((tile) => !tile.disabled);

    const disabled = !prevTile || prevTile.value === value;
    const onClick = () => onChange?.(prevTile?.value ?? "");
    return (
      <SideButton
        className={classes.tileButton}
        icon={IconType.ChevronUp}
        color={"primary"}
        disabled={disabled || externalDisabled}
        onClick={onClick}
      />
    );
  };

  const sideFillers = initialMaxVisiblePerSide - maxVisiblePerSide;
  const filler = Array.from({ length: sideFillers }).map((_, index) => (
    <div key={index} className={classes.emptyTile}>
      {renderTile(emptyTileData)}
    </div>
  ));

  return (
    <div className={classNames(classes.root, className)}>
      {filler}
      {needsFiller && <div className={classes.emptyTile}>{renderTile(emptyTileData)}</div>}
      {visibleTiles.map((tile, index) => (
        <div
          key={tile.value}
          className={classes.tile}
          style={{ transform: `scale(${1.0 - Math.abs(index - newCenterValueIndex) * 0.1})` }}
        >
          {index === newCenterValueIndex && (
            <div className={classes.centerTile}>
              {renderPrevButton()}
              {renderTile({
                data: tile,
                onClick: externalDisabled ? undefined : () => onChange?.(tile.value),
                selected: value === tile.value,
                error: error,
                disabled: externalDisabled,
              })}
              {renderNextButton()}
            </div>
          )}
          {index !== newCenterValueIndex &&
            renderTile({
              data: tile,
              onClick: () => onChange?.(tile.value),
              selected: false,
              disabled: externalDisabled,
            })}
        </div>
      ))}
      {filler}
    </div>
  );
};
